import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { WebhookSubscription } from "src/app/shared/models/focusDataModels/WebhookSubscription";
import { Order } from "src/app/shared/models/order";
import { environment } from "src/environments/environment";
import { CryptoService } from "./crypto.service";

@Injectable({
	providedIn: "root",
})
export class FocusdataService {
	baseUrl = `https://api.fpos.io`;
	domain = environment.production ? "focuslink.focuspos.com" : "dev.focuslink.focuspos.com";
	focusLinkDataRoute = `https://${this.domain}/v2`;

	constructor(private http: HttpClient, private crypto: CryptoService) {}

	async checksGet(venueKey: number, checkDate: string, minified: boolean): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/checks/${checkDate}?minified=${minified}`).toPromise();
	}

	async configMenuItemGetOne(venueKey: number, recordId: number): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/config/menu/items/${recordId}`).toPromise();
	}

	async configMenuItemGetAll(venueKey: number): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/config/menu/items`).toPromise();
	}

	async configMenuItemGetAllIdsOnly(venueKey: number): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/config/menu/items?id=true`).toPromise();
	}

	async coreConfigGet(venueKey: number): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/config/core`).toPromise();
	}

	async ooMenuRefresh(venueKey: number): Promise<any> {
		return this.http.patch(`${this.baseUrl}/stores/${venueKey}/data/config/menu/item/oosync`, undefined).toPromise();
	}

	async employeeList(venueKey: number, orderersOnly: boolean): Promise<any> {
		const query = orderersOnly ? "?orderentry=true" : "";
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/employees/list${query}`).toPromise();
	}

	async orderTypesGet(venueKey: number): Promise<any> {
		return this.http.get(`${this.focusLinkDataRoute}/stores/${venueKey}/data/config/ordertype`).toPromise();
	}

	async reportGroupGets(venueKey: number): Promise<any> {
		return this.http.get(`${this.focusLinkDataRoute}/stores/${venueKey}/data/config/reportgroups`).toPromise();
	}

	async revenueCentersGet(venueKey: number): Promise<any> {
		return this.http.get(`${this.focusLinkDataRoute}/stores/${venueKey}/data/config/revenuecenters`).toPromise();
	}

	async salesSummary(venueKey: number, checkDate: string): Promise<any> {
		return this.http.get(`${this.focusLinkDataRoute}/stores/${venueKey}/data/sales/${checkDate}/summary`).toPromise();
	}

	async stationsGet(venueKey: number): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/config/device/stations`).toPromise();
	}

	async taxesGet(venueKey: number): Promise<any> {
		return this.http.get(`${this.focusLinkDataRoute}/stores/${venueKey}/data/config/devices/taxes`).toPromise();
	}

	//
	//~ DATA IMPORT FILES
	//

	async dataImportFileGet(venueKey: number): Promise<any> {
		return this.http.get(`${this.baseUrl}/data/files/${venueKey}`).toPromise();
	}

	async dataImportFileGetOne(venueKey: number, fileName: string): Promise<any> {
		return this.http.get(`${this.baseUrl}/data/files/${venueKey}/${fileName}`).toPromise();
	}

	async dataImportFileDeleteOne(venueKey: number, fileName: string): Promise<any> {
		return this.http.delete(`${this.baseUrl}/data/files/${venueKey}/${fileName}`).toPromise();
	}

	//
	// ~EMPLOYEE MODULE
	//
	async employeeGetByAccessCode(venueKey: number, accessCode: string): Promise<any> {
		return await this.http.get(`${this.baseUrl}/stores/${venueKey}/data/employees/${accessCode}`).toPromise();
	}

	//
	// GUEST
	//

	async postGuest(venueKey: number, firstName: string, lastName: string, email: string, phone: string): Promise<any> {
		const body = {
			firstName: firstName,
			lastName: lastName,
			email: email,
			phone: phone,
		};

		return await this.http.post(`${this.baseUrl}/stores/${venueKey}/data/guest`, body).toPromise();
	}

	async postGuestVisit(venueKey: number, guestGuid: string, checkKey: string, order: Order): Promise<any> {
		return await this.http.post(`${this.baseUrl}/stores/${venueKey}/data/guest/${guestGuid}/visit/${checkKey}`, order).toPromise();
	}

	//
	//~ WEBHOOKS
	//
	async webhooksGet(venueKey: number): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/webhook`).toPromise();
	}

	async webhookGetSubscriptions(venueKey: number): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/webhook/subscription`).toPromise();
	}

	async webhookGetSubscriptionLicense(venueKey: number, licenseKey: string): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/webhook/subscription/${licenseKey}`).toPromise();
	}

	async webhookPostSubscription(venueKey: number, webhookSubscription: WebhookSubscription): Promise<any> {
		return this.http.post(`${this.baseUrl}/stores/${venueKey}/data/webhook/subscription`, webhookSubscription).toPromise();
	}

	async webhookDeleteSubscription(venueKey: number, licenseKey: string, webhookGuid: string): Promise<any> {
		return this.http.delete(`${this.baseUrl}/stores/${venueKey}/data/webhook/subscription/${licenseKey}/${webhookGuid}`).toPromise();
	}

	async yelpVenueId(venueKey: number, phoneNumber: string): Promise<any> {
		return this.http.get(`${this.baseUrl}/stores/${venueKey}/data/store/yelp?phone=${phoneNumber}`).toPromise();
	}

	//
	// ~ Sales data
	//

	async itemSalesGetAll(venueKey: number, checkDate: string, itemDetail: boolean): Promise<any> {
		return this.http.get(`${this.focusLinkDataRoute}/stores/${venueKey}/data/sales/items/${checkDate}?itemdetail=${itemDetail}`).toPromise();
	}
}
